/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { LoginCallback } from "@okta/okta-react";
import { Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "../../screens/dashboard/Dashboard";
import Loading from "./Loading";
import { RequiredAuth } from "./RequiredAuth";
import ManifestProDetails from "../../screens/manifest/manifestProDetails/ManifestProDetails";
import OverageEntry from "../../screens/overageEntry/OverageEntry";
import Unauthorized from "../../screens/unauthorized/unauthorized";
import SendAPB from "../../screens/sendToApb/sendapb";
import ExceptionDetails from "../../screens/exceptionDetails/exceptionDetails";
import AllShortageEntry from "../../screens/shortage/allShortagesEntry/allShortageEntry";
import DeliveredShortException from "../../screens/shortage/deliveryShortageInvestigation/deliveredShortException";
import BnfEntry from "../../screens/shortage/bnfEntry/bnfExceptions";
import MaintenancePage from "../../screens/maintenancePage/maintenance";
import Exceptions from "../../screens/exception/exceptions";
import ProtectedRoute from "./ProtectedRoute";

import {
  hasDamagesPermissionSelector,
  hasMisdeliveryPermissionSelector,
  hasOnHandDeliveryPermissionSelector,
  hasOveragesPermissionSelector,
  hasShortagesPermissionSelector,
} from "../../AppState/selectors/userPermissionsSelectors";
import DamageEntry from "../../screens/damagesAndRefusals/damageEntry";
import SendDisposition from "../../screens/sendDisposition/sendDisposition";
import OnHandDeliveryEntry from "../../screens/onHandDelivery/onHandDeliveryEntry";
import MisdeliveryEntry from "../../screens/misdelivery/misdeliveryEntry";
import BOLEmail from "../../screens/misdelivery/BOLEmail";

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="login/callback"
        element={<LoginCallback loadingElement={<Loading />} />}
      />
      <Route path="/401" element={<Unauthorized />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="/" element={<RequiredAuth />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        {/* New search route for Exceptions */}
        <Route path="/exceptions/search" element={<Exceptions />} />


        <Route path="/myAssignments" element={<Exceptions />} />
        <Route path="/exception/:id" element={<ExceptionDetails />} />
        <Route path="/exception/:id/apb/send" element={<SendAPB />} />
        <Route path="/manifest/:id" element={<ManifestProDetails />} />

        <Route element={<ProtectedRoute permissionSelector={hasOveragesPermissionSelector} />}>
          <Route path="/overages" element={<Exceptions />} />
          <Route path="/overage/entry/:osdNumber" element={<OverageEntry />} />
          <Route path="/overage/entry" element={<OverageEntry />} />
        </Route>
        
        <Route element={<ProtectedRoute permissionSelector={hasShortagesPermissionSelector} />}>
          <Route path="/shortages" element={<Exceptions />} />
          <Route path="/all-shortages/entry/:osdNumber" element={<AllShortageEntry />} />
          <Route path="/all-shortages/entry" element={<AllShortageEntry />} />
          <Route path="/delivered-short/entry/:osdNumber" element={<DeliveredShortException />} />
          <Route path="/bnf/entry/:osdNumber" element={<BnfEntry />} />
        </Route>

        <Route element={<ProtectedRoute permissionSelector={hasDamagesPermissionSelector} />}>
          <Route path="/damages-refusals" element={<Exceptions />} />
          <Route path="/:osdType/entry/:osdNumber" element={<DamageEntry />} />
        </Route>

        <Route element={<ProtectedRoute permissionSelector={hasOnHandDeliveryPermissionSelector} />}>
          <Route path="/on-hand-delivery" element={<Exceptions />} />
          <Route path="/on-hand-delivery/entry/:osdNumber" element={<OnHandDeliveryEntry />} />
          <Route path="/on-hand-delivery/entry" element={<OnHandDeliveryEntry />} />
        </Route>

        <Route element={<ProtectedRoute permissionSelector={hasMisdeliveryPermissionSelector} />}>
          <Route path="/misdelivery" element={<Exceptions />} />
          <Route path="/misdelivery/entry/:osdNumber" element={<MisdeliveryEntry />} />
          <Route path="/misdelivery/entry" element={<MisdeliveryEntry />} />
          <Route path="/misdelivery/bol/:osdNumber/send-email" element={<BOLEmail />} />
        </Route>

        <Route
          path="/exception/:osdNumber/send-disposition"
          element={<SendDisposition />}
        />

        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
